import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAllOperationMethods, useUserProfileDialog } from '@/widgets';
import { GlobalSearch } from '@/widgets/global-search';
import { AuthorizedLayoutHeader } from './AuthorizedLayoutHeader';
import { SidebarNavigation, useSidebarStore } from './sidebar';
import { cn } from '@/shared/utils';
import { useMediaQuery, usePullToRefresh } from '@/shared/lib';
import { useInternalTransferModals } from '@/features/internal-transfer';
import {
  useAccountActionsVisibility,
  useSyncAccountsStore,
} from '@/entities/account';
import {
  useAccountsAndOperationsRefresh,
  useSyncPendingAccounts,
} from './model';
import PullToRefresh from 'react-simple-pull-to-refresh';

const AuthorizedLayout = () => {
  useSyncPendingAccounts();
  useSyncAccountsStore();
  useAccountsAndOperationsRefresh();

  const { depositMethods, withdrawMethods } = useAllOperationMethods();
  const { visibility } = useAccountActionsVisibility();
  const { openUserProfileDialog } = useUserProfileDialog();

  const { openInternalTransferFormModal } = useInternalTransferModals();

  const { handleRefresh, isMobile } = usePullToRefresh();

  const [isScrolled, setIsScrolled] = useState(false);

  const { isCollapsed, isMobileOpen, closeMobile } = useSidebarStore();
  const { pathname } = useLocation();
  const isTabletUp = useMediaQuery('(min-width: 1068px)');

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    closeMobile();
  }, [pathname, closeMobile]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  useEffect(() => {
    if (isTabletUp) {
      closeMobile();
    }
  }, [isTabletUp, closeMobile]);

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      isPullable={isMobile}
      pullingContent={''}
      className="overflow-y-auto bg-background">
      <div className="flex flex-row w-screen h-dvh overflow-hidden">
        <div
          className={cn(
            'hidden tablet:block h-dvh shrink-0 transition-[width] duration-200',
            isCollapsed ? 'w-[5.6rem]' : 'w-[20.4rem]'
          )}>
          <SidebarNavigation variant={isCollapsed ? 'collapsed' : 'expanded'} />
        </div>

        <main className="grow min-w-0 flex flex-col bg-background overflow-hidden">
          <AuthorizedLayoutHeader
            onDepositClick={depositMethods.openOperationMethodsModal}
            onWithdrawClick={withdrawMethods.openOperationMethodsModal}
            onTransferClick={openInternalTransferFormModal}
            onSettingsClick={openUserProfileDialog}
            isScrolled={isScrolled}
            buttonsVisibility={{
              deposit: visibility.deposit,
              withdraw: visibility.withdraw,
              transfer: visibility.transfer,
            }}
          />
          <div
            ref={scrollContainerRef}
            onScroll={event => setIsScrolled(event.currentTarget.scrollTop > 0)}
            className={cn(
              'flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable] relative',
              isMobile && 'pb-[4.2rem]'
            )}>
            <Outlet />
          </div>

          <GlobalSearch />

          {isMobileOpen && (
            <div
              className="fixed inset-0 z-50 tablet:hidden"
              onClick={closeMobile}>
              <div className="absolute top-[5.6rem] right-[1.6rem] w-[24.9rem]">
                <div onClick={event => event.stopPropagation()}>
                  <SidebarNavigation
                    variant="mobile"
                    onItemClick={closeMobile}
                  />
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </PullToRefresh>
  );
};

export { AuthorizedLayout };
